import AbstractMediator from "../../../core/ui/AbstractMediator";
import EmailBoxType from "../model/EmailBoxType";
import EmailInteractionEvent from "../controller/EmailInteractionEvent";
import UnreadCounterView from "./UnreadCounterView";
import EmailAddedEvent from "../model/EmailAddedEvent";
import EmailConversationStatusChangedEvent from "../model/EmailConversationStatusChangedEvent";
import EmailModel from "../model/EmailModel";
import RevisableLanguagePack from "../../../../../lib/html5-software-library/src/ranj/language/RevisableLanguagePack";
import SoundManager from "../../../../../lib/html5-software-library/src/ranj/audio/SoundManager";
import EmailInterfaceEvent from "../model/EmailInterfaceEvent";

export default class EmailBoxSelectionMediator extends AbstractMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {EmailModel} emailModel 
     * @param {RevisableLanguagePack} languagePack 
     * @param {SoundManager} soundManager 
     */
    constructor(target, dispatcher, emailModel, languagePack, soundManager) {
        super(target, dispatcher);

        this._emailModel = emailModel;
        this._languagePack = languagePack;
        this._soundManager = soundManager;

        this._inboxCounter = new UnreadCounterView(this._target.inboxButton.indicator);
        this._draftsCounter = new UnreadCounterView(this._target.draftsButton.indicator);
        this._outboxCounter = new UnreadCounterView(this._target.outboxButton.indicator);
    }

    initialize() {
        super.initialize();
       
        this._inboxButton = this.createButton(this._target.inboxButton, this._handleInboxButtonClick.bind(this));
        this._draftsButton = this.createButton(this._target.draftsButton, this._handleDraftsButtonClick.bind(this));
        this._outboxButton = this.createButton(this._target.outboxButton, this._handleOutboxButtonClick.bind(this));

        this._boxToButtonMap = {
            [EmailBoxType.INBOX]: this._inboxButton,
            [EmailBoxType.DRAFTS]: this._draftsButton,
            [EmailBoxType.OUTBOX]: this._outboxButton,
        };

        this._selectMailboxButton(this._inboxButton);

        this._update();

        this._target.blurOverlay.visible = false;

        this.registerEventListener(EmailAddedEvent.EMAIL_ADDED_TO_CONVERSATION, this._update.bind(this));
        this.registerEventListener(EmailConversationStatusChangedEvent.STATUS_CHANGED, this._update.bind(this));
        this.registerEventListener(EmailInteractionEvent.SUBMIT_DRAFT, this._update.bind(this));
        this.registerEventListener(EmailInteractionEvent.OPEN_MAILBOX, this._handleOpenMailBox.bind(this));
        this.registerEventListener(EmailInterfaceEvent.LOCK_INTERFACE, this._handleLockInterface.bind(this));
        this.registerEventListener(EmailInterfaceEvent.UNLOCK_INTERFACE, this._handleUnlockInterface.bind(this));
    }

    destroy() {
        super.destroy();

        this._boxToButtonMap = null;

        this._inboxCounter = null;
        this._draftsCounter = null;
        this._outboxCounter = null;
    }

    _handleInboxButtonClick() {        
        this.dispatch(EmailInteractionEvent.OPEN_MAILBOX, {boxType: EmailBoxType.INBOX});
    }

    _handleDraftsButtonClick() {        
        this.dispatch(EmailInteractionEvent.OPEN_MAILBOX, {boxType: EmailBoxType.DRAFTS});
    }

    _handleOutboxButtonClick() {        
        this.dispatch(EmailInteractionEvent.OPEN_MAILBOX, {boxType: EmailBoxType.OUTBOX});
    }

    _update() {
        const inboxCount = this._emailModel.getUnreadCountForBoxType(EmailBoxType.INBOX);
        const draftCount = this._emailModel.getUnreadCountForBoxType(EmailBoxType.DRAFTS);

        this._inboxCounter.setCounter(inboxCount);
        this._draftsCounter.setCounter(draftCount);
        
        this._inboxButton.setLabel(this._languagePack.getText("email.boxSelection.inboxLabel"));
        this._draftsButton.setLabel(this._languagePack.getText("email.boxSelection.draftMailBoxLabel"));
        this._outboxButton.setLabel(this._languagePack.getText("email.boxSelection.sentMailBoxLabel"));
    }

    _handleOpenMailBox(evt) {
        const boxType = evt.params.boxType;

        const boxButton = this._boxToButtonMap[boxType];
        if (boxButton) {
            this._selectMailboxButton(boxButton);
        }
    }

    _selectMailboxButton(mailboxButton) {
        this._inboxButton.setSelected(false);
        this._draftsButton.setSelected(false);
        this._outboxButton.setSelected(false);

        mailboxButton.setSelected(true);
    }

    _handleLockInterface() {
        this._inboxButton.setEnabled(false);
        this._draftsButton.setEnabled(false);
        this._outboxButton.setEnabled(false);

        this._target.blurOverlay.visible = true;
    }

    _handleUnlockInterface() {
        this._inboxButton.setEnabled(true);
        this._draftsButton.setEnabled(true);
        this._outboxButton.setEnabled(true);

        this._target.blurOverlay.visible = false;
    }
}